import * as React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Typography from '@mui/material/Typography';
import { Translate, translateDateFormat } from '../../../../shared/LanguageService';
import RouterLink from '../../shared/view/RouterLink';
import { UnbookedOrder } from '../../types';

type UnbookedOrdersListProps = {
    orders: UnbookedOrder[];
    showBrokerFirm: boolean;
    returnTo: string;
};

function postalArea(order: UnbookedOrder) {
    return [order.postalNumber, order.city].filter(Boolean).join(' ');
}

function UnbookedOrderItem({
    order,
    showBrokerFirm,
    returnTo
}: { order: UnbookedOrder } & Omit<UnbookedOrdersListProps, 'orders'>) {
    const isAwaitingSe360 = order.listing === 'awaiting-se360';
    const details = [order.product, `${order.orderCollectionNumber}:${order.orderNumber}`]
        .filter(Boolean)
        .join(' · ');

    return (
        <Box
            component="li"
            sx={{
                listStyle: 'none',
                p: 1.5,
                borderRadius: 1,
                borderLeft: 3,
                borderColor: isAwaitingSe360 ? 'grey.400' : 'secondary.main',
                backgroundColor: isAwaitingSe360 ? 'grey.100' : 'background.paper',
                boxShadow: isAwaitingSe360 ? 'none' : 1,
                color: isAwaitingSe360 ? 'text.secondary' : 'text.primary'
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                <Typography variant="body2" sx={{ flex: 1, minWidth: 0, fontWeight: 600 }}>
                    {order.address || `${order.orderCollectionNumber}:${order.orderNumber}`}
                </Typography>
                <Button
                    size="small"
                    component={RouterLink}
                    to={`/orders/${order.orderId}/basic`}
                    sx={{ flexShrink: 0, minWidth: 0, mt: -0.5, mr: -1, py: 0.25, whiteSpace: 'nowrap' }}
                >
                    {Translate('customer-calendar/booking/go-to-order')}
                </Button>
            </Box>
            {postalArea(order) && <Typography variant="caption">{postalArea(order)}</Typography>}
            <Typography variant="caption" component="div">
                {details}
            </Typography>
            <Typography variant="caption" component="div">
                {Translate('customer-calendar/unbooked/ordered', {
                    date: translateDateFormat(new Date(order.orderedAt), 'PP')
                })}
            </Typography>
            {showBrokerFirm && order.brokerAgentName && (
                <Typography variant="caption" component="div">
                    {order.brokerAgentName}
                </Typography>
            )}
            <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
                {order.bookingPath && (
                    <Button
                        size="small"
                        variant="contained"
                        color="secondary"
                        component={RouterLink}
                        to={`${order.bookingPath}&redirectTo=${encodeURIComponent(returnTo)}`}
                        sx={{ '&:hover, &:focus': { color: 'secondary.contrastText' } }}
                    >
                        {Translate('customer-calendar/unbooked/book')}
                    </Button>
                )}
                {(isAwaitingSe360 || !order.bookingPath) && (
                    <Chip
                        size="small"
                        label={Translate('customer-calendar/unbooked/awaiting-se360')}
                    />
                )}
            </Box>
        </Box>
    );
}

function UnbookedOrdersList({ orders, showBrokerFirm, returnTo }: UnbookedOrdersListProps) {
    if (orders.length === 0) {
        return (
            <Typography variant="body2" color="text.secondary">
                {Translate('customer-calendar/unbooked/empty')}
            </Typography>
        );
    }

    return (
        <Box component="ul" sx={{ m: 0, p: 0, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {orders.map((order) => (
                <UnbookedOrderItem
                    key={order.orderId}
                    order={order}
                    showBrokerFirm={showBrokerFirm}
                    returnTo={returnTo}
                />
            ))}
        </Box>
    );
}

export default UnbookedOrdersList;
